import { Box } from "@mui/material";
import { Outlet, useParams } from "react-router-dom";
import { setDocumentTitle } from "~/lib/commonUtils";
import { PolicyAssignScope } from "./utils";
import { useGetPolicyQuery } from "~/operations";
import { PlatformPolicyHeader } from "~/pages/platform/policy/components/PlatformPolicyHeader";
import {
  TabListItem,
  TabNavigation,
  useRouteMatch,
} from "~/components/tab-nav";
import { PolicyIdToMrn } from "~/lib/mrn";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { ReactNode } from "react";
import { buildScopedUrl } from "./utils";

export interface PolicyDetailPageProps {
  scope: PolicyAssignScope;
  basePath: string;
  defaultTitle: string;
  renderBreadcrumbs: (title: string) => ReactNode;
}

export function PolicyDetailPage({
  scope,
  basePath,
  defaultTitle,
  renderBreadcrumbs,
}: PolicyDetailPageProps) {
  const params = useParams();
  const { isFeatureEnabled } = useFeatureFlags();
  const policyId = params.policyId!;

  const policyMrn = PolicyIdToMrn(policyId);

  const { loading, data, error } = useGetPolicyQuery({
    variables: { input: { mrn: policyMrn, spaceMrn: scope.mrn } },
  });

  if (loading) {
    return <LoadingPage what="policy" />;
  }

  const policy = data?.policy;

  if (error || !policy) {
    return <LoadingFailedPage what="policy" />;
  }

  const title = policy.name || defaultTitle;
  setDocumentTitle(title);
  const showExceptionsPropsTabs = isFeatureEnabled(
    "Platform Policy Exceptions",
  );

  const generateHref = (tab: string): string => {
    return buildScopedUrl(
      `${basePath}/${encodeURIComponent(policyId)}/${tab}`,
      scope,
    );
  };

  const tabList: TabListItem[] = [
    {
      label: "Overview",
      to: generateHref("overview"),
      route: "/overview",
    },
    {
      label: "Checks",
      to: generateHref("checks"),
      route: "/checks",
    },
    ...(showExceptionsPropsTabs
      ? [
          {
            label: "Exceptions",
            to: generateHref("exceptions"),
            route: "/exceptions",
          },
        ]
      : []),
  ];

  const currentTab = useRouteMatch(
    tabList.map((x) => x.route),
    "overview",
  );

  return (
    <Box>
      {renderBreadcrumbs(title)}
      <PlatformPolicyHeader scope={scope} policy={policy} />

      <Box>
        <TabNavigation
          {...{ id: "policy-tabs", tabList, currentTab, marginBottom: 3 }}
        />
        <Outlet
          context={{
            scope,
            policy,
            policyId,
          }}
        />
      </Box>
    </Box>
  );
}
